import React, { lazy, Suspense, useEffect, useState } from "react";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import BdrSectionPanel from "../../../../components/BdrSectionPanel/BdrSectionPanel";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  GET_SHOP_INVENTORY,
  GET_INVENTORY_MOVEMENTS,
  GET_INVENTORY_PRICE_HISTORY,
  GET_INVENTORY_TECHS,
} from "../../../../utils/GraphQL/queries";
import {
  ADD_SHOP_ITEM,
  UPDATE_SHOP_ITEM,
  DELETE_SHOP_ITEM,
  RESTORE_SHOP_ITEM,
  ADJUST_SHOP_ITEM,
  RECEIVE_INVENTORY_ITEM,
  UPSERT_VENDOR_SKU,
  REMOVE_VENDOR_SKU,
} from "../../../../utils/GraphQL/mutations";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import { useConfirm } from "../../../../utils/GlobalConfirm/GlobalConfirmContext";
import { gqlErrorCode, gqlErrorExtensions, gqlErrorMessage } from "../../../../utils/gqlErrorCode";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import TuneIcon from "@mui/icons-material/Tune";
import HistoryIcon from "@mui/icons-material/History";
import RestoreIcon from "@mui/icons-material/Restore";
import AddIcon from "@mui/icons-material/Add";
import "./adminShopInventory.css";
import BdrInput from "../../../../components/BdrInput/BdrInput";
import BdrCheckbox from "../../../../components/BdrCheckbox/BdrCheckbox";
import { contentsWarning } from "../../../../utils/inventoryContents";
import {
  ADJUST_REASONS,
  adjustReasonLabel,
  adjustReasonHelper,
  refinisherOptions,
  returnSaveEnabled,
  adjustUpDelta,
  adjustUpWarning,
} from "../../../../utils/inventoryReturn";
import { CssTextFieldStandard } from "../../../../components/CssTextFields/CssTextFieldStandard";
import {
  priceStrFormatter,
  removePriceStrFormatter,
  quantityFormatter,
} from "../../../../utils/formatters/priceStrFormatter";
import AdminBDRSpinner from "../../adminComponents/AdminBDRSpinner/AdminBDRSpinner";
import { QRCodeSVG } from "qrcode.react";

// Lazy-loaded so the Shopping List code path (component + its query + AdminBDRSpinner re-import)
// only enters the bundle when the user actually clicks the Shopping List tab.
const ShoppingListTab = lazy(() => import("./ShoppingListTab"));
// Receipts (inventory hardening Phase 3): review/read of mobile-captured receipts;
// confirm = the receive. Same lazy pattern. No upload on the web (D10).
const ReceiptsTab = lazy(() => import("./ReceiptsTab"));

// Inventory hardening (2026-09-18, plans/inventory_system_hardening_2026_09_18.md):
// - the catalog is fetched WITH inactive rows (includeInactive: true) so a
//   soft-deleted item can be shown under "Show inactive" and restored;
// - quantity is no longer edited on the item form — "Adjust" writes a ledger
//   row with a reason (+ note, optional only for a physical count);
// - every adjust mints ONE idempotency key per dialog and reuses it on retry;
// - the Adjust dialog also records a RETURN from a tech (receiveInventoryItem
//   + returnFromUserId: stock up AND that tech's usage corrected) and warns
//   when an adjust would raise stock for a non-purchase reason, offering
//   "Record as Receive instead" (plan_inventory_cost_reporting_2026_10_01 §C/§D).
//   Each mode has its OWN idempotency key: the server dedupes on the key alone;
// - coded server errors are read through gqlErrorCode (never message text);
// - a tap on the history icon opens the movement ledger + price observations;
// - vendor SKUs (pack sizes) are edited on the item form (receipt matching
//   upserts them automatically; this is the manual path).

const blankForm = {
  itemName: "",
  currentQuantity: "",
  maxQuantity: "",
  price: "",
  priceStr: "",
  defaultTechQty: "",
  stockUnit: "each",
  // Contents (recipe usage units, 2026-09-27): 1 stock unit = N pieces.
  piecesPerUnit: "",
  pieceName: "",
};

// Largest vendor pack (purchase unit -> stock units) on an item, 0 when none.
const vendorPackOf = (item) =>
  Math.max(0, ...((item?.vendorSkus || []).map((sk) => parseInt(sk.unitsPerPurchase, 10) || 0)));

// ADJUST_REASONS (value / label / helper) lives in utils/inventoryReturn.js.
const blankAdjust = {
  newQuantity: "", reason: "count", note: "", key: "",
  // Return mode: a DELTA ("3 came back"), never the absolute pre-fill.
  isReturn: false, returnQty: "", returnTechId: "", returnKey: "", receiveKey: "",
};
const blankSku = { vendor: "", sku: "", unitsPerPurchase: "1" };

const fmtWhen = (ms) => {
  const n = parseInt(ms, 10);
  if (!Number.isFinite(n)) return "";
  return new Date(n).toLocaleString("en-US", {
    timeZone: "America/Chicago",
    month: "numeric",
    day: "numeric",
    year: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  });
};

const movementLabel = (m) => {
  switch (m.type) {
    case "receive":
      return "Received";
    case "distribute":
      return `Issued to ${m.subjectName || "tech"}`;
    case "return":
      return `Returned by ${m.subjectName || "tech"}`;
    case "adjust":
      return `Adjusted${m.reason ? ` (${adjustReasonLabel(m.reason)})` : ""}`;
    default:
      return m.type;
  }
};

/* The tab strip is data. It was three copy-pasted <button> blocks whose only
   difference was a key and a label. */
const TABS = [
  { key: "inventory", label: "Inventory" },
  { key: "shopping", label: "Shopping List" },
  { key: "receipts", label: "Receipts" },
];

const AdminShopInventory = () => {
  const { handleSnack } = useSnackbar();
  const { confirm } = useConfirm();
  // Main tab — "inventory" (catalog CRUD) | "shopping" (Shopping List report)
  // | "receipts" (review + confirm). The last two lazy-load component AND query.
  const [activeTab, setActiveTab] = useState("inventory");
  const [shopItems, setShopItems] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null); // null = create mode
  const [formData, setFormData] = useState({ ...blankForm });
  // One rule for "contents looks wrong", shared with mobile + the save guard.
  const contentsNote = contentsWarning({
    piecesPerUnit: formData.piecesPerUnit,
    pieceName: formData.pieceName,
    stockUnit: formData.stockUnit,
    vendorPack: vendorPackOf(editingItem),
  });
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrItem, setQrItem] = useState(null);
  const [qrPrintAllOpen, setQrPrintAllOpen] = useState(false);

  // Adjust dialog
  const [adjustItem, setAdjustItem] = useState(null);
  const [adjust, setAdjust] = useState({ ...blankAdjust });
  const [adjustError, setAdjustError] = useState("");
  const [adjusting, setAdjusting] = useState(false);
  // Refinishers for "Return from a tech" (lean query; empty list hides the switch).
  const [techs, setTechs] = useState([]);
  const [fetchTechs] = useLazyQuery(GET_INVENTORY_TECHS, { fetchPolicy: "cache-first" });

  // History drawer
  const [historyItem, setHistoryItem] = useState(null);
  const [historyTab, setHistoryTab] = useState("movements");

  // Vendor SKU editor (inside the edit modal)
  const [skuDraft, setSkuDraft] = useState({ ...blankSku });
  const [skuBusy, setSkuBusy] = useState(false);

  const [fetchInventory] = useLazyQuery(GET_SHOP_INVENTORY);
  const [fetchMovements, { data: movementsData, loading: movementsLoading }] = useLazyQuery(
    GET_INVENTORY_MOVEMENTS,
    { fetchPolicy: "network-only" }
  );
  const [fetchPrices, { data: pricesData, loading: pricesLoading }] = useLazyQuery(
    GET_INVENTORY_PRICE_HISTORY,
    { fetchPolicy: "network-only" }
  );
  const [addItem] = useMutation(ADD_SHOP_ITEM);
  const [updateItem] = useMutation(UPDATE_SHOP_ITEM);
  const [deleteItem] = useMutation(DELETE_SHOP_ITEM);
  const [restoreItem] = useMutation(RESTORE_SHOP_ITEM);
  const [adjustItemMut] = useMutation(ADJUST_SHOP_ITEM);
  const [receiveItemMut] = useMutation(RECEIVE_INVENTORY_ITEM);
  const [upsertSku] = useMutation(UPSERT_VENDOR_SKU);
  const [removeSku] = useMutation(REMOVE_VENDOR_SKU);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadItems();
    fetchTechs()
      .then(({ data }) => setTechs(refinisherOptions(data?.getAllUsers)))
      .catch(() => {}); // picker just stays hidden; adjusting still works
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadItems = async () => {
    setLoading(true);
    try {
      // Full catalog, active AND inactive — the list filters on showInactive.
      const { data } = await fetchInventory({
        variables: { includeInactive: true },
        fetchPolicy: "network-only",
      });
      const items = data?.getInventory || [];
      const sorted = [...items].sort((a, b) =>
        (a.itemName || "").localeCompare(b.itemName || "", undefined, { sensitivity: "base" })
      );
      setShopItems(sorted);
      // Keep an open edit modal's SKU list fresh after an upsert/remove.
      if (editingItem) {
        const fresh = sorted.find((i) => i.id === editingItem.id);
        if (fresh) setEditingItem(fresh);
      }
    } catch (err) {
      console.error("Error fetching inventory:", err);
    }
    setLoading(false);
  };

  const visibleItems = showInactive ? shopItems : shopItems.filter((i) => i.active !== false);
  const inactiveCount = shopItems.filter((i) => i.active === false).length;

  // --- Form Handlers ---
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "price") {
      const numStr = removePriceStrFormatter(value);
      setFormData((prev) => ({
        ...prev,
        price: numStr,
        priceStr: priceStrFormatter(numStr),
      }));
      return;
    }

    if (name === "piecesPerUnit") {
      // Decimal allowed (1 gallon = 128 oz); one dot max.
      const cleaned = value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
      setFormData((prev) => ({ ...prev, piecesPerUnit: cleaned }));
      return;
    }

    if (
      name === "currentQuantity" ||
      name === "maxQuantity" ||
      name === "defaultTechQty"
    ) {
      setFormData((prev) => ({
        ...prev,
        [name]: quantityFormatter(value),
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const openCreateModal = () => {
    setEditingItem(null);
    setFormData({ ...blankForm });
    setFormError("");
    setSkuDraft({ ...blankSku });
    setModalOpen(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      itemName: item.itemName || "",
      currentQuantity: item.currentQuantity || "",
      maxQuantity: item.maxQuantity || "",
      price: item.price || "",
      priceStr: priceStrFormatter(item.price) || "",
      defaultTechQty: item.defaultTechQty || "",
      stockUnit: item.stockUnit || "each",
      // Seeded so an unrelated edit re-sends the stored pair instead of clearing it.
      piecesPerUnit: item.piecesPerUnit || "",
      pieceName: item.pieceName || "",
    });
    setFormError("");
    setSkuDraft({ ...blankSku });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingItem(null);
    setFormData({ ...blankForm });
    setFormError("");
  };

  // Same name exists but is deactivated — offer Restore instead of a dupe.
  const offerRestore = async (existingId, itemName) => {
    const ok = await confirm({
      title: "Inactive item",
      message: `"${itemName}" exists but is deactivated. Restore it?`,
      confirmLabel: "Restore",
    });
    if (!ok) return false;
    return handleRestore(existingId, { silent: true });
  };

  const handleSubmit = async () => {
    setFormError("");
    const { itemName, currentQuantity, maxQuantity, price, stockUnit } = formData;

    if (!itemName.trim()) {
      setFormError("Item name is required");
      return;
    }
    if (!editingItem && !currentQuantity) {
      setFormError("Opening quantity is required");
      return;
    }
    if (!maxQuantity) {
      setFormError("Max quantity is required");
      return;
    }
    if (!price) {
      setFormError("Price per unit is required");
      return;
    }
    const ppu = (formData.piecesPerUnit || "").trim();
    const pieceName = (formData.pieceName || "").trim();
    if (!!ppu !== !!pieceName) {
      setFormError("Contents needs both a count and a piece name (e.g. 100 filters), or neither");
      return;
    }
    if (ppu && !(parseFloat(ppu) >= 0.001 && parseFloat(ppu) <= 100000)) {
      setFormError("Contents count must be a number from 0.001 to 100000");
      return;
    }
    const cw = contentsWarning({ piecesPerUnit: ppu, pieceName, stockUnit, vendorPack: vendorPackOf(editingItem) });
    if (cw?.block) {
      setFormError(cw.message);
      return;
    }

    setSubmitting(true);

    try {
      if (editingItem) {
        // Update — quantity is NOT sent; it goes through Adjust.
        const { data } = await updateItem({
          variables: {
            itemId: editingItem.id,
            maxQuantity,
            price,
            defaultTechQty: formData.defaultTechQty || undefined,
            stockUnit: (stockUnit || "each").trim() || "each",
            // Always both, literal: "" clears on the server.
            piecesPerUnit: ppu,
            pieceName,
          },
        });
        if (data?.updateInventoryItem) {
          handleSnack("Item Updated", 2000);
          closeModal();
          loadItems();
        }
      } else {
        // Create
        const { data } = await addItem({
          variables: {
            itemName: itemName.trim(),
            currentQuantity,
            maxQuantity,
            price,
            defaultTechQty: formData.defaultTechQty || undefined,
            stockUnit: (stockUnit || "each").trim() || "each",
            piecesPerUnit: ppu,
            pieceName,
          },
        });
        if (data?.addInventoryItem) {
          handleSnack("Item Created", 2000);
          closeModal();
          loadItems();
        }
      }
    } catch (err) {
      const code = gqlErrorCode(err);
      if (code === "INACTIVE_ITEM_EXISTS") {
        const ext = gqlErrorExtensions(err);
        setSubmitting(false);
        closeModal();
        const restored = await offerRestore(ext?.existingId, itemName.trim());
        if (restored) handleSnack("Item Restored", 2000);
        return;
      }
      console.error("Error saving inventory item:", err);
      setFormError(gqlErrorMessage(err));
      handleSnack(gqlErrorMessage(err), 3000);
    }

    setSubmitting(false);
  };

  // Soft delete — "Deactivate". History is kept; Restore is available.
  /* No two-click arm any more: `BdrButton variant="danger"` gates this behind
     the app's shared confirm dialog, so by the time this runs the user has
     said yes in a dialog that named the item. */
  const handleDelete = async (itemId) => {
    try {
      const { data } = await deleteItem({ variables: { itemId } });
      if (data?.deleteInventoryItem) {
        handleSnack("Item Deactivated", 2000);
        loadItems();
      }
    } catch (err) {
      console.error("Error deactivating inventory item:", err);
      handleSnack(gqlErrorMessage(err, "Failed to deactivate item"), 3000);
    }
  };

  const handleRestore = async (itemId, { silent } = {}) => {
    try {
      const { data } = await restoreItem({ variables: { itemId } });
      if (data?.restoreInventoryItem) {
        if (!silent) handleSnack("Item Restored", 2000);
        loadItems();
        return true;
      }
    } catch (err) {
      console.error("Error restoring inventory item:", err);
      handleSnack(gqlErrorMessage(err, "Failed to restore item"), 3000);
    }
    return false;
  };

  // --- Adjust (ledger row with a reason) ---
  const mintKey = (itemId) => `web_${itemId}_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;

  const openAdjust = (item) => {
    setAdjustItem(item);
    setAdjust({
      ...blankAdjust,
      newQuantity: item.currentQuantity || "0",
      // One key per dialog PER MODE: a retry after a dropped response is a
      // no-op, and a return after an applied adjust is never deduped away.
      key: mintKey(item.id),
      returnKey: mintKey(item.id),
      receiveKey: mintKey(item.id),
    });
    setAdjustError("");
  };

  const closeAdjust = () => {
    setAdjustItem(null);
    setAdjust({ ...blankAdjust });
    setAdjustError("");
  };

  const submitAdjust = async () => {
    setAdjustError("");
    const qty = parseInt(adjust.newQuantity, 10);
    if (!Number.isFinite(qty)) {
      setAdjustError("Enter the new quantity");
      return;
    }
    const noteRequired = adjust.reason !== "count";
    if (noteRequired && !adjust.note.trim()) {
      setAdjustError("A note is required for this reason");
      return;
    }
    setAdjusting(true);
    try {
      const { data } = await adjustItemMut({
        variables: {
          itemId: adjustItem.id,
          newQuantity: String(qty),
          reason: adjust.reason,
          note: adjust.note.trim() || undefined,
          idempotencyKey: adjust.key,
          clientReportedAt: String(Date.now()),
        },
      });
      if (data?.adjustInventoryItem) {
        handleSnack("Quantity Adjusted", 2000);
        closeAdjust();
        loadItems();
      }
    } catch (err) {
      console.error("Error adjusting inventory item:", err);
      setAdjustError(gqlErrorMessage(err));
    }
    setAdjusting(false);
  };

  // Shared by "Record return" (tech named) and the guard's "Record as Receive
  // instead" (no tech). An ITEM_INACTIVE here means another admin deactivated
  // the item after the list loaded: plain error + refresh, no Restore loop
  // (Adjust is unreachable on inactive rows).
  const submitReceive = async ({ quantityToAdd, returnFromUserId, idempotencyKey, successLabel }) => {
    setAdjustError("");
    setAdjusting(true);
    try {
      const { data } = await receiveItemMut({
        variables: {
          itemId: adjustItem.id,
          quantityToAdd: String(quantityToAdd),
          idempotencyKey,
          returnFromUserId: returnFromUserId || undefined,
          clientReportedAt: String(Date.now()),
        },
      });
      const after = data?.receiveInventoryItem?.currentQuantity;
      if (after != null) {
        handleSnack(`${successLabel}: ${adjustItem.currentQuantity} → ${after}`, 2500);
        closeAdjust();
        loadItems();
      }
    } catch (err) {
      console.error("Error recording inventory receive/return:", err);
      setAdjustError(gqlErrorMessage(err));
      if (gqlErrorCode(err) === "ITEM_INACTIVE") loadItems();
    }
    setAdjusting(false);
  };

  const submitReturn = () => {
    if (!returnSaveEnabled(adjust.returnQty, adjust.returnTechId)) return;
    return submitReceive({
      quantityToAdd: adjust.returnQty.trim(),
      returnFromUserId: adjust.returnTechId,
      idempotencyKey: adjust.returnKey,
      successLabel: "Return recorded",
    });
  };

  const upDelta = adjustItem
    ? adjustUpDelta({ reason: adjust.reason, newQuantity: adjust.newQuantity, currentQuantity: adjustItem.currentQuantity })
    : 0;

  const submitReceiveInstead = () => {
    if (!upDelta) return;
    return submitReceive({
      quantityToAdd: upDelta,
      returnFromUserId: null,
      idempotencyKey: adjust.receiveKey,
      successLabel: "Received",
    });
  };

  // --- History drawer ---
  const openHistory = (item) => {
    setHistoryItem(item);
    setHistoryTab("movements");
    fetchMovements({ variables: { itemId: item.id, limit: 100 } });
    fetchPrices({ variables: { itemId: item.id } });
  };

  // --- Vendor SKUs (pack sizes) on the edit modal ---
  const submitSku = async () => {
    if (!editingItem) return;
    const vendor = skuDraft.vendor.trim();
    if (!vendor) {
      setFormError("Vendor is required for a SKU");
      return;
    }
    setSkuBusy(true);
    try {
      await upsertSku({
        variables: {
          itemId: editingItem.id,
          vendor,
          sku: skuDraft.sku.trim() || undefined,
          unitsPerPurchase: String(Math.max(1, parseInt(skuDraft.unitsPerPurchase, 10) || 1)),
        },
      });
      setSkuDraft({ ...blankSku });
      setFormError("");
      await loadItems();
    } catch (err) {
      setFormError(gqlErrorMessage(err));
    }
    setSkuBusy(false);
  };

  const deleteSku = async (entry) => {
    if (!editingItem) return;
    setSkuBusy(true);
    try {
      await removeSku({
        variables: { itemId: editingItem.id, vendor: entry.vendor, sku: entry.sku || undefined },
      });
      await loadItems();
    } catch (err) {
      setFormError(gqlErrorMessage(err));
    }
    setSkuBusy(false);
  };

  const movements = movementsData?.getInventoryMovements?.items || [];
  const prices = pricesData?.getInventoryPriceHistory || [];

  return (
    <div className="shop-inv-page">
      <BdrSectionPanel
        className="shop-inv-panel"
        title="Shop Inventory"
        headerClassName="shop-inv-hdr"
        bodyClassName="shop-inv-body"
        /* Both of these act on the CATALOG — Print All QR prints it, Create
           adds to it — so they render only while the Inventory tab is showing,
           as they did before. */
        actions={
          activeTab === "inventory" ? (
            <>
              <BdrButton
                size="sm"
                variant="action"
                icon={<QrCode2Icon />}
                collapseToIcon
                onClick={() => setQrPrintAllOpen(true)}
              >
                Print All QR
              </BdrButton>
              {/* `confirm`, not `confirm-solid`. The header band is already a
                  solid surface of its own, and a gradient-filled button on it
                  reads as a second surface stacked on the first rather than a
                  control sitting in it. The semi-transparent weight lets the
                  band show through, which is what every other control in a
                  panel header does. */}
              <BdrButton
                size="sm"
                variant="confirm"
                icon={<AddIcon />}
                collapseToIcon
                onClick={openCreateModal}
              >
                Create
              </BdrButton>
            </>
          ) : null
        }
        headerExtra={
          <div className="bdr-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`bdr-tab${
                  activeTab === tab.key ? " bdr-tab-active" : ""
                }`}
                aria-pressed={activeTab === tab.key}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        }
      >
        {activeTab === "inventory" && (
          loading ? (
            <div className="shop-inv-loading">
              <AdminBDRSpinner message="Loading..." />
            </div>
          ) : shopItems.length === 0 ? (
            <p className="shop-inv-empty">No inventory items found.</p>
          ) : (
            <div className="shop-inv-table-wrap">
              {inactiveCount > 0 && (
                <div className="shop-inv-filter-row">
                  <BdrCheckbox
                    size="sm"
                    className="shop-inv-filter-label"
                    label={`Show inactive (${inactiveCount})`}
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                  />
                </div>
              )}
              <table className="shop-inv-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Max</th>
                    <th>Current</th>
                    <th>Default Tech Dist Qty</th>
                    <th>Price / unit</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleItems.map((item) => {
                    const inactive = item.active === false;
                    const qty = parseInt(item.currentQuantity, 10) || 0;
                    return (
                      <tr key={item.id} className={inactive ? "shop-inv-row-inactive" : ""}>
                        <td>
                          {item.itemName}
                          {inactive && <span className="shop-inv-inactive-pill">inactive</span>}
                        </td>
                        <td>{item.maxQuantity}</td>
                        <td className={qty < 0 ? "shop-inv-qty-negative" : ""}>
                          {item.currentQuantity} {item.stockUnit && item.stockUnit !== "each" ? item.stockUnit : ""}
                        </td>
                        <td>{item.defaultTechQty || "—"}</td>
                        <td>{priceStrFormatter(item.price)}</td>
                        {/*
                          ROW ACTIONS — `BdrButton iconOnly`, not five
                          hand-rolled `<button>`s.

                          They were all `.shop-inv-action-btn`: a transparent
                          box with a 1px white border, identical for every one
                          of them, with the DESTRUCTIVE action told apart only
                          by the colour of its glyph. Each also set
                          `sx={{ fontSize: 18 }}` inline; `.bdr-btn--icon`
                          sizes its own glyph against its own padding, so that
                          goes too.

                          `title` carries each action's name — these are
                          icon-only at every width, so the tooltip IS the label,
                          and `BdrButton` mirrors it onto `aria-label`.
                        */}
                        <td className="shop-inv-actions-cell">
                          <BdrButton
                            size="sm"
                            variant="action"
                            iconOnly
                            icon={<HistoryIcon />}
                            title="History (ledger + prices)"
                            onClick={() => openHistory(item)}
                          />
                          {inactive ? (
                            <BdrButton
                              size="sm"
                              variant="confirm"
                              iconOnly
                              icon={<RestoreIcon />}
                              title="Restore"
                              onClick={() => handleRestore(item.id)}
                            />
                          ) : (
                            <>
                              <BdrButton
                                size="sm"
                                variant="action"
                                iconOnly
                                icon={<QrCode2Icon />}
                                title="QR Code"
                                onClick={() => {
                                  setQrItem(item);
                                  setQrDialogOpen(true);
                                }}
                              />
                              <BdrButton
                                size="sm"
                                variant="action"
                                iconOnly
                                icon={<TuneIcon />}
                                title="Adjust quantity"
                                onClick={() => openAdjust(item)}
                              />
                              <BdrButton
                                size="sm"
                                variant="action"
                                iconOnly
                                icon={<EditIcon />}
                                title="Edit"
                                onClick={() => openEditModal(item)}
                              />
                              {/* `danger` confirms by default, which replaces
                                  the two-click arm this row carried: the first
                                  click swapped the title to "Click again to
                                  confirm" and an `onBlur` silently disarmed it
                                  — a button that changed meaning and changed
                                  back without saying so. The dialog names the
                                  item and says the delete is a soft one. */}
                              <BdrButton
                                size="sm"
                                variant="danger"
                                iconOnly
                                icon={<DeleteIcon />}
                                title="Deactivate"
                                confirmTitle="Deactivate item?"
                                confirmLabel="Deactivate"
                                note={`"${item.itemName}" is hidden from the catalog. Its stock and history are kept, and "Show inactive" can restore it.`}
                                onClick={() => handleDelete(item.id)}
                              />
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        )}

        {activeTab === "shopping" && (
          <Suspense
            fallback={
              <div className="shop-inv-loading">
                <AdminBDRSpinner message="Loading shopping list..." />
              </div>
            }
          >
            <ShoppingListTab />
          </Suspense>
        )}

        {activeTab === "receipts" && (
          <Suspense
            fallback={
              <div className="shop-inv-loading">
                <AdminBDRSpinner message="Loading receipts..." />
              </div>
            }
          >
            {/* A confirm receives stock — refetch the catalog so the Inventory tab is current. */}
            <ReceiptsTab onStockChanged={loadItems} />
          </Suspense>
        )}
      </BdrSectionPanel>

      {/* Create / Edit Modal */}
      <BdrModal
        open={modalOpen}
        onClose={closeModal}
        variant="panel"
        size="md"
        title={editingItem ? "Edit Item" : "Create Item"}
        panelClassName="shop-inv-dialog"
        actions={
          <>
            <BdrButton onClick={closeModal}>Cancel</BdrButton>
            <BdrButton
              variant="confirm-solid"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? "Saving..." : editingItem ? "Update" : "Create"}
            </BdrButton>
          </>
        }
      >
        {formError && (
          <p className="bdr-alert bdr-alert--danger bdr-alert--start" role="alert">
            {formError}
          </p>
        )}

        <BdrInput
          className="shop-inv-f-full"
          label="Item Name"
          name="itemName"
          value={formData.itemName}
          onChange={handleChange}
          disabled={!!editingItem}
        />
        {editingItem ? (
          <div className="shop-inv-stock-strip">
            <span>
              In stock: <strong>{editingItem.currentQuantity}</strong> {editingItem.stockUnit || "each"}
            </span>
            <button
              type="button"
              className="shop-inv-link-btn"
              onClick={() => { closeModal(); openAdjust(editingItem); }}
            >
              <TuneIcon sx={{ fontSize: 14, mr: 0.5 }} /> Adjust
            </button>
          </div>
        ) : (
          <BdrInput
            className="shop-inv-f-full"
            label="Opening Quantity"
            name="currentQuantity"
            value={formData.currentQuantity}
            onChange={handleChange}
            inputMode="numeric" pattern="[0-9]*"
          />
        )}
        <BdrInput
          className="shop-inv-f-full"
          label="Max Quantity"
          name="maxQuantity"
          value={formData.maxQuantity}
          onChange={handleChange}
          inputMode="numeric" pattern="[0-9]*"
        />
        <BdrInput
          className="shop-inv-f-full"
          label="Default Tech Dist Qty"
          name="defaultTechQty"
          value={formData.defaultTechQty}
          onChange={handleChange}
          inputMode="numeric" pattern="[0-9]*"
        />
        <div className="shop-inv-price-row">
          <BdrInput
            className="shop-inv-f-grow"
            label="Price per unit"
            name="price"
            value={formData.priceStr}
            onChange={handleChange}
            inputMode="decimal"
          />
          <BdrInput
            className="shop-inv-f-unit"
            label="Stock unit"
            name="stockUnit"
            value={formData.stockUnit}
            onChange={handleChange}
            placeholder="each / can / gal"
          />
        </div>

        <div className="shop-inv-sku-panel">
          <div className="shop-inv-sku-title">Contents (optional)</div>
          <div className="shop-inv-sku-help">
            How many usable pieces are in <em>one</em> {formData.stockUnit || "each"}? Only used to enter and read
            material recipes per door (1 pack = 100 filters lets you type "1.5 filters per door"). Leave blank for
            items used whole.
          </div>
          <div className="shop-inv-contents-row">
            <span className="shop-inv-contents-eq">1 {formData.stockUnit || "each"} =</span>
            <CssTextFieldStandard
              sx={{ width: 110, mb: 2 }}
              label="Count"
              name="piecesPerUnit"
              value={formData.piecesPerUnit}
              onChange={handleChange}
              inputProps={{ inputMode: "decimal" }}
              placeholder="100"
            />
            <CssTextFieldStandard
              sx={{ flex: 1, mb: 2 }}
              label="Piece name"
              name="pieceName"
              value={formData.pieceName}
              onChange={handleChange}
              inputProps={{ maxLength: 24 }}
              placeholder="filter"
            />
          </div>
          {contentsNote && <div className="shop-inv-contents-warn">{contentsNote.message}</div>}
        </div>

        {editingItem && (
          <div className="shop-inv-sku-panel">
            <div className="shop-inv-sku-title">Vendor SKUs &amp; pack sizes</div>
            <div className="shop-inv-sku-help">
              1 receipt unit of a SKU = <em>pack size</em> stock units. Receipt matching fills these in
              automatically; edit here only when a pack size is wrong.
            </div>
            {(editingItem.vendorSkus || []).length === 0 ? (
              <div className="shop-inv-sku-empty">No vendor SKUs yet.</div>
            ) : (
              <ul className="shop-inv-sku-list">
                {editingItem.vendorSkus.map((s) => (
                  <li key={`${s.vendor}|${s.sku || ""}`}>
                    <span className="shop-inv-sku-vendor">{s.vendor}</span>
                    <span className="shop-inv-sku-code">{s.sku || "(no SKU)"}</span>
                    <span className="shop-inv-sku-pack">× {s.unitsPerPurchase}</span>
                    <button
                      type="button"
                      className="shop-inv-link-btn"
                      disabled={skuBusy}
                      onClick={() => deleteSku(s)}
                    >
                      remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="shop-inv-sku-form">
              <BdrInput
                className="shop-inv-f-grow"
                label="Vendor"
                value={skuDraft.vendor}
                onChange={(e) => setSkuDraft((p) => ({ ...p, vendor: e.target.value }))}
              />
              <BdrInput
                className="shop-inv-f-grow"
                label="SKU"
                value={skuDraft.sku}
                onChange={(e) => setSkuDraft((p) => ({ ...p, sku: e.target.value }))}
              />
              <BdrInput
                className="shop-inv-f-pack"
                label="Pack"
                value={skuDraft.unitsPerPurchase}
                onChange={(e) =>
                  setSkuDraft((p) => ({ ...p, unitsPerPurchase: quantityFormatter(e.target.value) }))
                }
                inputMode="numeric" pattern="[0-9]*"
              />
              <BdrButton
                variant="confirm-solid"
                disabled={skuBusy}
                onClick={submitSku}
              >
                Save SKU
              </BdrButton>
            </div>
          </div>
        )}
      </BdrModal>

      {/* Adjust quantity — a ledger row with a reason */}
      <BdrModal
        open={!!adjustItem}
        onClose={closeAdjust}
        variant="panel"
        size="sm"
        title={`Adjust — ${adjustItem?.itemName || ""}`}
        panelClassName="shop-inv-dialog"
        actions={
          <>
            <BdrButton onClick={closeAdjust} disabled={adjusting}>
              Cancel
            </BdrButton>
            {/* Two different writes behind one dialog, so the primary button
                changes with the mode rather than the mode changing what one
                label does. */}
            {adjust.isReturn ? (
              <BdrButton
                variant="confirm-solid"
                onClick={submitReturn}
                disabled={
                  adjusting ||
                  !returnSaveEnabled(adjust.returnQty, adjust.returnTechId)
                }
              >
                {adjusting ? "Saving..." : "Record return"}
              </BdrButton>
            ) : (
              <BdrButton
                variant="confirm-solid"
                onClick={submitAdjust}
                disabled={adjusting}
              >
                {adjusting ? "Saving..." : "Save adjustment"}
              </BdrButton>
            )}
          </>
        }
      >
        {/* No <h2> here: the shell's header already carries
            "Adjust — <item>", and main's inline one would duplicate it. */}
        <p className="shop-inv-dialog-sub">
          Currently {adjustItem?.currentQuantity}{" "}
          {adjustItem?.stockUnit || "each"}.{" "}
          {adjust.isReturn
            ? "Goes back into shop stock and off this refinisher's usage. Not an expense."
            : "Changes shop stock only. Not an expense; no refinisher is charged."}
        </p>
        {adjustError && (
          <p className="bdr-alert bdr-alert--danger bdr-alert--start" role="alert">
            {adjustError}
          </p>
        )}

        {/* Only offered when there is someone to return FROM. */}
        {techs.length > 0 && (
          <BdrCheckbox
            label="Return from a tech"
            checked={adjust.isReturn}
            disabled={adjusting}
            onChange={(e) => {
              const on = e.target.checked;
              setAdjust((p) => ({
                ...p,
                isReturn: on,
                returnTechId: on ? p.returnTechId : "",
                returnQty: on ? p.returnQty : "",
              }));
              setAdjustError("");
            }}
          />
        )}

        {adjust.isReturn ? (
          <>
            <BdrInput
              className="shop-inv-f-full"
              label="Refinisher"
              placeholder="Choose refinisher"
              value={adjust.returnTechId}
              disabled={adjusting}
              onChange={(e) =>
                setAdjust((p) => ({ ...p, returnTechId: e.target.value }))
              }
              options={techs}
            />
            <BdrInput
              className="shop-inv-f-full"
              label="Quantity returned"
              value={adjust.returnQty}
              disabled={adjusting}
              onChange={(e) =>
                setAdjust((p) => ({
                  ...p,
                  returnQty: e.target.value.replace(/[^\d]/g, ""),
                }))
              }
              inputMode="numeric"
              helper={`${adjustItem?.currentQuantity} → ${
                (parseInt(adjustItem?.currentQuantity, 10) || 0) +
                (parseInt(adjust.returnQty, 10) || 0)
              } ${adjustItem?.stockUnit || "each"}`}
            />
          </>
        ) : (
          <>
            <BdrInput
              className="shop-inv-f-full"
              label="New quantity"
              value={adjust.newQuantity}
              disabled={adjusting}
              onChange={(e) =>
                setAdjust((p) => ({
                  ...p,
                  newQuantity: e.target.value.replace(/[^-\d]/g, ""),
                }))
              }
              inputMode="numeric"
            />
            {/* Counting MORE than the system thought is almost never a
                correction — it is stock that arrived and was never received,
                and recording it as a count loses the cost. */}
            {upDelta > 0 && (
              <div className="shop-inv-guard bdr-alert bdr-alert--warning bdr-alert--start">
                <span>
                  {adjustUpWarning({
                    delta: upDelta,
                    currentQuantity: adjustItem?.currentQuantity,
                  })}
                </span>
                <BdrButton
                  size="sm"
                  variant="action"
                  disabled={adjusting}
                  onClick={submitReceiveInstead}
                >
                  Record as Receive instead
                </BdrButton>
              </div>
            )}
            <BdrInput
              className="shop-inv-f-full"
              label="Reason"
              value={adjust.reason}
              disabled={adjusting}
              onChange={(e) =>
                setAdjust((p) => ({ ...p, reason: e.target.value }))
              }
              options={ADJUST_REASONS}
              helper={adjustReasonHelper(adjust.reason)}
            />
            <BdrInput
              className="shop-inv-f-full"
              label={
                adjust.reason === "count" ? "Note (optional)" : "Note (required)"
              }
              value={adjust.note}
              disabled={adjusting}
              onChange={(e) => setAdjust((p) => ({ ...p, note: e.target.value }))}
            />
          </>
        )}
      </BdrModal>

      {/* History — movement ledger + price observations */}
      <BdrModal
        open={!!historyItem}
        onClose={() => setHistoryItem(null)}
        variant="panel"
        size="lg"
        title={historyItem?.itemName}
        panelClassName="shop-inv-dialog"
        actions={
          <BdrButton onClick={() => setHistoryItem(null)}>Close</BdrButton>
        }
      >
        <p className="shop-inv-dialog-sub">
          {historyItem?.currentQuantity} {historyItem?.stockUnit || "each"} in stock ·{" "}
          {priceStrFormatter(historyItem?.price)} / {historyItem?.stockUnit || "each"}
        </p>
        {/* The History dialog's own sub-tabs. Same `.bdr-tab*` recipe as the
            page strip — it is the one tab control in the app that is not in a
            panel header, and there is no reason for it to look different. */}
        <div className="bdr-tabs shop-inv-history-tabs">
          <button
            className={`bdr-tab${historyTab === "movements" ? " bdr-tab-active" : ""}`}
            onClick={() => setHistoryTab("movements")}
          >
            Movements ({movements.length})
          </button>
          <button
            className={`bdr-tab${historyTab === "prices" ? " bdr-tab-active" : ""}`}
            onClick={() => setHistoryTab("prices")}
          >
            Prices ({prices.length})
          </button>
        </div>
        <div className="shop-inv-history-body">
          {historyTab === "movements" && (
            movementsLoading ? (
              <AdminBDRSpinner message="Loading ledger..." />
            ) : movements.length === 0 ? (
              <p className="shop-inv-empty">No movements yet.</p>
            ) : (
              <table className="shop-inv-table shop-inv-history-table">
                <thead>
                  <tr>
                    <th>Δ</th>
                    <th>What</th>
                    <th>Before → After</th>
                    <th>Unit cost</th>
                    <th>Note</th>
                    <th>When</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.map((m) => {
                    const d = parseInt(m.qtyDelta, 10) || 0;
                    return (
                      <tr key={m.id}>
                        <td className={d >= 0 ? "shop-inv-delta-pos" : "shop-inv-delta-neg"}>
                          {d >= 0 ? `+${d}` : d}
                        </td>
                        <td>{movementLabel(m)}</td>
                        <td>{m.qtyBefore} → {m.qtyAfter}</td>
                        <td>
                          {m.unitCostCents != null ? priceStrFormatter(m.unitCostCents) : "—"}
                          {m.costSource === "receipt" ? " (receipt)" : ""}
                        </td>
                        <td>{m.note || "—"}</td>
                        <td>{fmtWhen(m.occurredAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )
          )}
          {historyTab === "prices" && (
            pricesLoading ? (
              <AdminBDRSpinner message="Loading prices..." />
            ) : prices.length === 0 ? (
              <p className="shop-inv-empty">No price history yet.</p>
            ) : (
              <table className="shop-inv-table shop-inv-history-table">
                <thead>
                  <tr>
                    <th>Price / unit</th>
                    <th>Source</th>
                    <th>Vendor</th>
                    <th>When</th>
                  </tr>
                </thead>
                <tbody>
                  {prices.map((p) => (
                    <tr key={p.id}>
                      <td>{priceStrFormatter(p.priceCents)}</td>
                      <td>{p.source}</td>
                      <td>{p.vendor || "—"}</td>
                      <td>{fmtWhen(p.observedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
        </div>
      </BdrModal>

      {/* Print All QR Codes */}
      <BdrModal
        open={qrPrintAllOpen}
        onClose={() => setQrPrintAllOpen(false)}
        size="lg"
        /* The only modal on this page that keeps its own header bar. The shell
           header is styled for a dark panel and this sheet is white, and the
           toolbar has to disappear at print time — which `@media print` does by
           class. Its Print/Close are the way out, so nothing is trapped. */
        showHeader={false}
        ariaLabel="Print all QR codes"
        /* WHITE on purpose — this is a print sheet, not a dark admin panel.
           The QR codes have to come out of the printer on white. */
        surface="#fff"
        panelClassName="shop-inv-print-dialog"
      >
        <div className="qr-print-all-toolbar">
          <BdrButton onClick={() => setQrPrintAllOpen(false)}>Close</BdrButton>
          <BdrButton variant="confirm-solid" onClick={() => window.print()}>
            Print
          </BdrButton>
        </div>
        <div className="qr-print-all-grid">
          {shopItems.filter((i) => i.active !== false).map((item) => (
            <div key={item.id} className="qr-print-all-cell">
              <div className="qr-print-all-qr">
                <QRCodeSVG
                  value={`inventoryItem-${item.id}`}
                  size={62}
                  level="M"
                />
              </div>
              <div className="qr-print-all-info">
                <span className="qr-print-all-name">{item.itemName}</span>
              </div>
            </div>
          ))}
        </div>
      </BdrModal>

      {/* QR Code Modal */}
      <BdrModal
        open={qrDialogOpen}
        onClose={() => setQrDialogOpen(false)}
        variant="panel"
        size="sm"
        title={`QR Code — ${qrItem?.itemName || ""}`}
        panelClassName="shop-inv-dialog"
        actions={
          <>
            <BdrButton onClick={() => setQrDialogOpen(false)}>Close</BdrButton>
            <BdrButton variant="confirm-solid" onClick={() => window.print()}>
              Print
            </BdrButton>
          </>
        }
      >
        <div className="shop-inv-qr-body">
          {qrItem && (
            <div className="shop-inv-qr-frame">
              <QRCodeSVG
                value={`inventoryItem-${qrItem.id}`}
                size={250}
                level="H"
              />
            </div>
          )}
          <p className="shop-inv-qr-name">{qrItem?.itemName}</p>
          <p className="shop-inv-qr-id">ID: {qrItem?.id}</p>
        </div>
      </BdrModal>
    </div>
  );
};

export default AdminShopInventory;
